import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { PATIENT_STATUSES, SMS_STATUSES } from 'features/Home/constants/infoConstants'
import { useFetchScheduleDayQuery } from 'features/Schedule/api/schedule.api'
import { scheduleGridQueryOptions } from 'features/Schedule/api/scheduleQueryOptions'
import {
  filterCanceledSchedules,
  sortCanceledSchedules,
} from 'features/Schedule/pages/components/utils'
import { computeScheduleDayDisabledIntervals } from 'features/Schedule/pages/components/utils/computeScheduleDayDisabledIntervals'
import { useMemo } from 'react'
import { useParams } from 'react-router'

dayjs.extend(customParseFormat)

export const useScheduleData = (
  date: string,
  selectedConsultationStaffId: string,
  showCanceled = true,
) => {
  const { siteId } = useParams()

  const {
    currentData: scheduleDay,
    isFetching: isFetchingDay,
    isLoading: isLoadingDay,
  } = useFetchScheduleDayQuery(
    { siteId, date },
    {
      skip: !date,
      ...scheduleGridQueryOptions,
    },
  )

  const rooms = useMemo(() => scheduleDay?.rooms ?? [], [scheduleDay?.rooms])

  const schedules = useMemo(() => {
    if (!scheduleDay) return []

    const staffFilteredItems =
      !selectedConsultationStaffId || selectedConsultationStaffId === '-1'
        ? scheduleDay.items
        : scheduleDay.items.filter(
            (item) => item.consultation_staff === Number(selectedConsultationStaffId),
          )

    return sortCanceledSchedules(filterCanceledSchedules(staffFilteredItems, showCanceled))
  }, [scheduleDay, selectedConsultationStaffId, showCanceled])

  const consultationStaffs = useMemo(
    () => scheduleDay?.consultation_staffs ?? [],
    [scheduleDay?.consultation_staffs],
  )

  const languages = useMemo(() => scheduleDay?.languages ?? [], [scheduleDay?.languages])

  const timeInterval = useMemo(() => {
    if (!scheduleDay || scheduleDay?.time_interval?.min === null) return { min: null, max: null }
    return {
      min: dayjs(scheduleDay.time_interval.min, 'HH:mm:ss').format('HH:mm'),
      max: dayjs(scheduleDay.time_interval.max, 'HH:mm:ss').format('HH:mm'),
    }
  }, [scheduleDay])

  const defaultAppointmentTime = useMemo(
    () => scheduleDay?.time_interval?.default_appointment_time ?? 0,
    [scheduleDay],
  )

  const disabledIntervals = useMemo(() => {
    if (!scheduleDay || !date) return []
    return computeScheduleDayDisabledIntervals(date, scheduleDay.rooms, scheduleDay.time_interval)
  }, [scheduleDay, date])

  const eventsToSendNotification = useMemo(() => {
    return schedules
      .filter(
        (item) =>
          item.sms_notification === SMS_STATUSES.UNCONFIRMED &&
          item.status.code === PATIENT_STATUSES.UNCONFIRMED,
      )
      .map((item) => item.id)
  }, [schedules])

  const roomStatuses = useMemo(() => scheduleDay?.room_statuses ?? [], [scheduleDay?.room_statuses])

  const patientStatuses = useMemo(
    () => scheduleDay?.patient_statuses ?? [],
    [scheduleDay?.patient_statuses],
  )

  const doctorBufferTime = useMemo(
    () => scheduleDay?.doctor_buffer_time ?? { before: null, after: null },
    [scheduleDay?.doctor_buffer_time],
  )

  const appointmentCategories = useMemo(
    () => scheduleDay?.appointment_categories ?? [],
    [scheduleDay?.appointment_categories],
  )

  const hasEhrBooking = !!scheduleDay?.has_ehr_booking
  const hasOccupied = !!scheduleDay?.has_occupied
  const byPassTemperatureCheck = scheduleDay?.pandemic_protocol === 0
  const appointmentColor = scheduleDay?.appointment_color ?? 'status'
  const punctuality = scheduleDay?.punctuality ?? []
  const patientBufferTime = scheduleDay?.patient_buffer_time ?? { after: 0, before: 0 }

  return {
    scheduleDay,
    rooms,
    schedules,
    consultationStaffs,
    languages,
    timeInterval,
    disabledIntervals,
    isFetchingDay,
    isLoadingDay,
    eventsToSendNotification,
    roomStatuses,
    doctorBufferTime,
    defaultAppointmentTime,
    patientStatuses,
    appointmentCategories,
    hasEhrBooking,
    hasOccupied,
    byPassTemperatureCheck,
    appointmentColor,
    punctuality,
    patientBufferTime,
  }
}
